import type { BreadcrumbItem } from '@/cms/fields/types/common'
import { useCMSPageField } from '@/cms/fields'
import { mqMin, space, tok } from '@sunwebgroup/ui-components'
import { Button, Icon, Typography } from '@sunwebgroup/ui-components/uds'
import { styled } from 'styled-components'

const Wrapper = styled.div`
  display: flex;
  gap: ${space('primitives_space_4')};
  align-items: center;
`

const IconPin = styled(Icon)`
  color: ${tok('semantics_content_secondary_color')};
  margin-right: ${space('primitives_space_4')};
`

const ListWrapper = styled.ol`
  list-style: none;
  flex-wrap: wrap;
  display: flex;
  align-items: center;
  margin: unset;
  padding: unset;

  li {
    display: flex;
    align-items: center;
  }

  li + li::before {
    content: '·';
    padding: 0 ${space('primitives_space_4')};
    color: ${tok('semantics_content_secondary_color')};
  }
`

const StyledTypography = styled(Typography)`
  color: ${tok('semantics_content_secondary_color')};
  padding: ${space('primitives_space_4')} 0;
  ${Typography.modVariant('body-s-regular-flat')}

  @media ${mqMin('primitives_breakpoint_m_min')} {
    ${Typography.modVariant('body-s-regular-flat')}
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    ${Typography.modVariant('body-m-regular-tight')}
  }
`

const ButtonElement = styled(Button)`
  ${Typography} {
    ${Typography.modVariant('body-s-regular-flat')}
    text-decoration-line: underline;

    &:not(:hover) {
      color: ${tok('semantics_content_secondary_color')};
    }

    @media ${mqMin('primitives_breakpoint_m_min')} {
      ${Typography.modVariant('body-s-regular-flat')}
    }

    @media ${mqMin('primitives_breakpoint_l_min')} {
      ${Typography.modVariant('body-m-regular-tight')}
    }
  }
`

export function Breadcrumb() {
  const data = useCMSPageField('Breadcrumb.elements')
  let elements: BreadcrumbItem[] = []

  if (!data) {
    return
  }

  if (data) {
    elements = data.map(item => ({
      text: item.text,
      href: item.href,
    }))
  }

  return (
    <Wrapper>
      <ListWrapper itemScope itemType="http://schema.org/BreadcrumbList" aria-label="Breadcrumb">
        {elements.map((item, index) => {
          return (
            <li itemProp="itemListElement" itemScope itemType="http://schema.org/ListItem" key={item.text}>
              {index === elements.length - 1 || !item.href
                ? (
                    <StyledTypography itemProp="name">{item.text}</StyledTypography>
                  )
                : (
                    <>
                      {index === 0 && <IconPin glyph="MapPin" size={16} />}
                      <ButtonElement itemProp="item" href={item.href} forwardedAs="a" variant="transparentNeutral" size="XS">
                        <Typography itemProp="name">{item.text}</Typography>
                      </ButtonElement>
                    </>
                  )}
              <meta itemProp="position" content={`${index + 1}`} />
            </li>
          )
        })}
      </ListWrapper>
    </Wrapper>
  )
}
